<template>
  <main class="cb-page rules-screen">
    <header class="rules-screen__header"><h1>Правила</h1></header>
    <ModeSwitch class="rules-screen__tabs" :model-value="rulesTab" @update:model-value="rulesTab = $event" />

    <section class="rule-explanation" aria-labelledby="rule-title">
      <div class="rule-visual">
        <img v-for="visual in RULE_VISUALS" :key="visual.mode" :src="visual.url" class="rule-visual__image" :class="{ 'rule-visual__image--active': rulesTab === visual.mode }" :alt="rulesTab === visual.mode ? modeContent.alt : ''" :aria-hidden="rulesTab !== visual.mode ? 'true' : undefined" width="1000" height="500" decoding="async" />
        <div class="rule-visual__badges" aria-hidden="true">
          <span class="rule-visual__badge"><span class="rule-visual__badge-icon"><AppIcon name="check" :size="17" /></span>Держись</span>
          <span class="rule-visual__badge rule-visual__badge--fail"><span class="rule-visual__badge-icon"><AppIcon name="close" :size="17" /></span>{{ modeContent.violation }}</span>
        </div>
      </div>
      <h2 id="rule-title" class="rule-explanation__title">{{ modeContent.title }}</h2>
      <p v-if="rulesTab === 'MOUTH'" class="rule-explanation__timing"><AppIcon name="info" :size="16" />Открой рот до конца отсчёта.</p>
      <p class="rule-explanation__note">{{ modeContent.note }}</p>
    </section>

    <section class="rules-preparation" aria-labelledby="before-start-title">
      <h2 id="before-start-title">Перед стартом</h2>
      <ol class="rules-preparation__list">
        <li class="rules-step">
          <span class="rules-step__number" aria-hidden="true">1</span>
          <img class="rules-step__art" :src="cameraArt" width="70" height="50" alt="" />
          <div><h3>Разреши камеру</h3><p>Дай доступ к камере в приложении.</p></div>
        </li>
        <li class="rules-step">
          <span class="rules-step__number" aria-hidden="true">2</span>
          <div class="rules-step__art rules-step__art--face" aria-hidden="true"><img :src="blinkPair" alt="" /><span></span></div>
          <div><h3>Лицо в кадре, хороший свет</h3><p>Твоё лицо должно быть хорошо освещено.</p></div>
        </li>
        <li class="rules-step">
          <span class="rules-step__number" aria-hidden="true">3</span>
          <img class="rules-step__art" :src="countdownArt" width="70" height="50" alt="" />
          <div><h3>Пройди калибровку</h3><p>До старта нарушения не считаются.</p></div>
        </li>
      </ol>
    </section>

    <details class="rules-details">
      <summary>
        <AppIcon name="document" :size="22" class="rules-details__icon" />
        <span><strong>Подробные правила</strong><small>Калибровка, связь и честная игра</small></span>
        <AppIcon name="chevron-right" :size="18" class="rules-details__chevron" />
      </summary>
      <div class="rules-details__body">
        <section><h2>Как играть</h2><ol><li v-for="(step, index) in STEPS" :key="index">{{ step }}</li></ol></section>
        <section v-for="mode in MODES" :key="mode.value"><h2>{{ mode.title }}</h2><p class="rules-details__lead">{{ mode.lead }}</p><ul><li v-for="(item, index) in mode.items" :key="index">{{ item }}</li></ul></section>
        <section v-for="section in SECTIONS" :key="section.id"><h2>{{ section.title }}</h2><ul><li v-for="(item, index) in section.items" :key="index">{{ item }}</li></ul></section>
        <section>
          <h2>Награды</h2>
          <p>За победу в обычной дуэли начисляется {{ WIN_STARS }} звёзд.</p>
          <ul>
            <li>Звёзды копятся в «Хранилище» — баланс виден на главном экране и в профиле.</li>
            <li>За поражение звёзды не списываются и не начисляются.</li>
            <li>Победы в обычных дуэлях учитываются в лидерборде. Место зависит от результатов всех игроков.</li>
            <li>Игра с другом проходит без звёзд и изменений общей статистики и рейтинга.</li>
          </ul>
        </section>
        <button type="button" class="rules-screen__policy" @click="openAgreement">Политика конфиденциальности и пользовательское соглашение</button>
      </div>
    </details>
    <button type="button" class="rules-screen__back" @click="goBack"><AppIcon name="chevron-left" :size="18" />Назад</button>
  </main>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import AppIcon from "@/components/ui/AppIcon.vue";
import ModeSwitch from "@/components/ui/ModeSwitch.vue";
import { useSessionStore } from "@/stores/session";
import blinkPair from "@/assets/img/layout-pack/rules-blink-pair.jpg";
import mouthPair from "@/assets/img/layout-pack/rules-open-mouth-pair.jpg";
import cameraArt from "@/assets/img/layout-pack/permission-camera.svg";
import countdownArt from "@/assets/img/layout-pack/countdown.svg";

const router = useRouter();
const session = useSessionStore();
// This is an explanation tab; browsing the rules must not change the next game.
const rulesTab = ref(session.mode === "MOUTH" ? "MOUTH" : "BLINK");
const RULE_VISUALS = Object.freeze([{ mode: "BLINK", url: blinkPair }, { mode: "MOUTH", url: mouthPair }]);
const RULE_CONTENT = Object.freeze({
  BLINK: { title: "Моргнул первым — проиграл.", note: "Даже быстрое моргание считается.", violation: "Моргнул", alt: "Слева глаза открыты, справа показано моргание." },
  MOUTH: { title: "Закрыл рот первым — проиграл.", note: "В этом режиме моргать можно.", violation: "Закрыл рот", alt: "Слева рот открыт, справа рот закрыт." },
});
const modeContent = computed(() => RULE_CONTENT[rulesTab.value]);
const WIN_STARS = 40;
const STEPS = Object.freeze([
  "Выберите режим на главном экране: «Моргание» или «Открытый рот».",
  "Нажмите «Начать игру» и разрешите доступ к камере — мы подберём соперника.",
  "Посмотрите на соперника и пройдите короткую калибровку.",
  "После старта держитесь: кто первым нарушит правило режима — проиграл.",
]);
const MODES = Object.freeze([
  {
    value: "BLINK", title: "Моргание", lead: "Кто первым моргнёт — проиграл.",
    items: ["Держите глаза открытыми, пока соперник не моргнёт.", "Засчитывается даже быстрое моргание.", "Сильный прищур тоже может засчитаться как моргание."],
  },
  {
    value: "MOUTH", title: "Открытый рот", lead: "Кто первым закроет рот — проиграл.",
    items: ["Откройте рот ещё во время отсчёта и держите его открытым до конца раунда.", "Сомкнутые губы засчитываются как поражение, даже если рот закрылся ненадолго.", "Моргать в этом режиме можно сколько угодно."],
  },
]);
const SECTIONS = Object.freeze([
  {
    id: "calibration", title: "Калибровка",
    items: [
      "Перед стартом идёт обратный отсчёт от 5 до 1 — приложение запоминает ваше лицо в спокойном состоянии.",
      "Смотрите прямо в камеру и держите лицо внутри овала.",
      "Нужен хороший свет: лицо не должно быть в тени или против окна.",
      "Во время калибровки нарушения не засчитываются.",
    ],
  },
  {
    id: "fair", title: "Честная игра",
    items: [
      "Камера должна видеть ваше лицо всю игру. Если лица нет в кадре, появится предупреждение «Лицо не в кадре».",
      "Выход из игры, закрытие приложения или обрыв связи дольше 10 секунд отменяют партию — победа никому не засчитывается.",
      "Для проверки нарушения на сервер отправляется снимок; оба игрока видят короткий повтор без звука и ключевой кадр.",
      "Живые видео и звук между соперниками передаются по защищённой связи. Для повтора временно сохраняется короткая последовательность кадров с камеры без звука.",
    ],
  },
]);
function goBack() {
  const back = window.history.state && window.history.state.back;
  if (back) router.back();
  else router.push("/");
}
function openAgreement() { router.push("/agreement"); }
</script>

<style scoped>
.rules-screen { max-width: 460px; margin-inline: auto; gap: 12px; --cb-text-2: #b6bab1; }
.rules-screen__header { padding: 5px 0 2px; }
.rules-screen__header h1 { font-size: 28px; line-height: 1.15; letter-spacing: -.025em; }
.rules-screen__tabs { padding: 3px; border-radius: 14px; box-shadow: none; }
.rules-screen__tabs :deep(.mode-switch__option) { height: 44px; gap: 7px; padding-inline: 7px; font-size: 13px; }
.rules-screen__tabs :deep(.mode-switch__thumb) { top: 3px; bottom: 3px; left: 3px; width: calc(50% - 3px); border-radius: 11px; box-shadow: none; }
.rule-explanation { display: grid; gap: 10px; }
.rule-visual { position: relative; aspect-ratio: 2 / 1; overflow: hidden; border-radius: 16px; background: var(--cb-surface); }
.rule-visual__image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0; transition: opacity 200ms ease; }
.rule-visual__image--active { opacity: 1; }
.rule-visual__badges { position: absolute; left: 8px; right: 8px; bottom: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.rule-visual__badge { justify-self: start; display: inline-flex; align-items: center; gap: 6px; padding-right: 10px; border-radius: 999px; background: rgba(18,24,11,.85); color: var(--cb-lime); font-size: 12px; font-weight: 650; line-height: 1; white-space: nowrap; }
.rule-visual__badge-icon { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; background: var(--cb-lime); color: #111509; }
.rule-visual__badge--fail { color: #ff987e; background: rgba(43,25,18,.85); }
.rule-visual__badge--fail .rule-visual__badge-icon { background: #ff9277; }
.rule-explanation__title { font-size: 20px; font-weight: 750; line-height: 1.25; letter-spacing: -.015em; text-align: center; }
.rule-explanation__note { color: var(--cb-text-2); font-size: 13px; line-height: 1.4; text-align: center; }
.rule-explanation__timing { display: flex; align-items: center; justify-content: center; gap: 7px; padding: 7px 8px; border-radius: 9px; background: var(--cb-lime); color: var(--cb-on-lime); font-size: 12px; line-height: 1.3; font-weight: 700; text-align: center; }
.rules-preparation { display: grid; gap: 10px; margin-top: 3px; padding-top: 13px; border-top: 1px solid var(--cb-border-strong); }
.rules-preparation h2 { font-size: 17px; font-weight: 700; }
.rules-preparation__list { display: grid; gap: 8px; margin: 0; padding: 0; list-style: none; }
.rules-step { display: grid; grid-template-columns: 27px 70px minmax(0,1fr); align-items: center; gap: 10px; min-height: 74px; padding: 9px 10px; border: 1px solid var(--cb-border-strong); border-radius: 14px; background: linear-gradient(140deg,rgba(255,255,255,.035),rgba(255,255,255,.012)); }
.rules-step__number { width: 27px; height: 27px; display: grid; place-items: center; border-radius: 50%; background: var(--cb-lime); color: var(--cb-on-lime); font-size: 13px; font-weight: 700; }
.rules-step__art { position: relative; width: 70px; height: 50px; object-fit: contain; overflow: hidden; border-radius: 8px; background: #252b20; }
.rules-step__art--face img { position: absolute; width: 200%; height: 100%; max-width: none; object-fit: cover; }
.rules-step__art--face span { position: absolute; inset: 5px 8px; background: linear-gradient(var(--cb-lime),var(--cb-lime)) left top / 8px 1px no-repeat, linear-gradient(var(--cb-lime),var(--cb-lime)) left top / 1px 8px no-repeat, linear-gradient(var(--cb-lime),var(--cb-lime)) right top / 8px 1px no-repeat, linear-gradient(var(--cb-lime),var(--cb-lime)) right top / 1px 8px no-repeat, linear-gradient(var(--cb-lime),var(--cb-lime)) left bottom / 8px 1px no-repeat, linear-gradient(var(--cb-lime),var(--cb-lime)) left bottom / 1px 8px no-repeat, linear-gradient(var(--cb-lime),var(--cb-lime)) right bottom / 8px 1px no-repeat, linear-gradient(var(--cb-lime),var(--cb-lime)) right bottom / 1px 8px no-repeat; }
.rules-step h3 { font-size: 13px; font-weight: 650; line-height: 1.2; }
.rules-step p { margin-top: 3px; color: var(--cb-text-2); font-size: 12px; line-height: 1.3; }
.rules-details { border: 1px solid var(--cb-border-strong); border-radius: 14px; background: var(--cb-surface); overflow: hidden; }
.rules-details summary { display: flex; align-items: center; gap: 10px; min-height: 62px; padding: 10px 12px; cursor: pointer; list-style: none; }
.rules-details summary::-webkit-details-marker { display: none; }
.rules-details__icon { color: var(--cb-lime); }
.rules-details summary > span { min-width: 0; flex: 1; }
.rules-details strong { display: block; font-size: 14px; line-height: 1.25; font-weight: 650; }
.rules-details small { display: block; color: var(--cb-text-2); font-size: 12px; line-height: 1.35; margin-top: 3px; }
.rules-details__chevron { color: var(--cb-text-2); transition: transform 200ms ease; }
.rules-details[open] .rules-details__chevron { transform: rotate(90deg); }
.rules-details__body { display: grid; gap: 16px; padding: 14px; border-top: 1px solid var(--cb-border); }
.rules-details__body h2 { margin-bottom: 7px; font-size: 16px; }
.rules-details__body p, .rules-details__body li { font-size: 14px; line-height: 1.45; color: var(--cb-text-2); }
.rules-details__body ul, .rules-details__body ol { display: grid; gap: 7px; padding-left: 18px; margin-top: 7px; }
.rules-details__lead { font-weight: 650; }
.rules-screen__policy { min-height: 44px; padding: 7px 0; border: 0; background: transparent; color: var(--cb-text-2); font: 500 13px/1.4 var(--cb-font); text-align: left; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.rules-screen__back { display: flex; align-items: center; justify-content: center; gap: 5px; min-height: 44px; border: 0; background: transparent; color: var(--cb-text-2); font: 500 14px/1.3 var(--cb-font); cursor: pointer; }
@media (max-width: 350px) {
  .rules-screen__tabs :deep(.mode-switch__option) { font-size: 12px; gap: 5px; }
  .rule-visual__badge { font-size: 11px; gap: 4px; padding-right: 7px; }
  .rule-visual__badge-icon { width: 26px; height: 26px; }
  .rule-explanation__title { font-size: 19px; }
  .rules-step { grid-template-columns: 25px 56px minmax(0,1fr); gap: 8px; padding: 9px 8px; }
  .rules-step__art { width: 56px; height: 46px; }
  .rules-step h3 { font-size: 12px; }
  .rules-step p { font-size: 11px; }
}
@media (prefers-reduced-motion: reduce) {
  .rule-visual__image, .rules-details__chevron, .rules-screen__tabs :deep(.mode-switch__thumb) { transition: none; }
}
</style>